import React, { useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { PlusIcon, XIcon } from '@heroicons/react/solid';

const FIELD_TYPES = ['text', 'single-choice', 'multi-choice'];

export default function AddFieldForm({ onSubmit, onCancel }) {
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState(FIELD_TYPES[0]);
  const [options, setOptions] = useState(['']);
  const [error, setError] = useState('');

  const inputClasses = "border mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-violet-500 focus:ring-violet-500 sm:text-sm px-4 py-3";

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!fieldName.trim()) {
      setError('Field name is required');
      return;
    }

    if (['single-choice', 'multi-choice'].includes(fieldType)) {
      const validOptions = options.filter(opt => opt.trim());
      if (validOptions.length < 2) {
        setError('At least two options are required');
        return;
      }
      onSubmit({
        name: fieldName.trim(),
        type: fieldType,
        options: validOptions
      });
    } else {
      onSubmit({
        name: fieldName.trim(),
        type: fieldType
      });
    }
  };

  const handleAddOption = () => {
    setOptions([...options, '']);
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleRemoveOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 bg-red-50 text-sm text-red-600 rounded-lg">
          {error}
        </div>
      )}

      {/* Field Name */}
      <div className="space-y-1.5">
        <label htmlFor="fieldName" className="block text-sm font-medium text-gray-700">
          Field Name
        </label>
        <input
          type="text"
          id="fieldName"
          value={fieldName}
          onChange={(e) => setFieldName(e.target.value)}
          className={inputClasses}
          placeholder="e.g., GP Name"
        />
      </div>

      {/* Field Type */}
      <div className="space-y-1.5">
        <label htmlFor="fieldType" className="block text-sm font-medium text-gray-700">
          Field Type
        </label>
        <select
          id="fieldType"
          value={fieldType}
          onChange={(e) => setFieldType(e.target.value)}
          className={`${inputClasses} pr-10`}
        >
          {FIELD_TYPES.map(type => (
            <option key={type} value={type}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Options (for single/multi choice) */}
      {['single-choice', 'multi-choice'].includes(fieldType) && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Answer Options
          </label>
          <div className="space-y-2">
            {options.map((option, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  placeholder={`Option ${index + 1}`}
                  className={inputClasses}
                />
                {options.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveOption(index)}
                    className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-150"
                  >
                    <XIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={handleAddOption}
            className="inline-flex items-center px-3 py-1.5 text-sm text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded-lg transition-colors duration-150"
          >
            <PlusIcon className="h-4 w-4 mr-1.5" />
            Add Option
          </button>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4">
        <AlertDialog.Cancel asChild>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 transition-colors duration-150"
          >
            Cancel
          </button>
        </AlertDialog.Cancel>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-violet-600 border border-transparent rounded-lg hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 transition-colors duration-150"
        >
          Add Field
        </button>
      </div>
    </form>
  );
}